import '@adyen/adyen-web/dist/adyen.css'
import { Modal } from '@components/Modal'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  Spinner,
  Text,
  XStack,
  YStack,
  useWindowDimensions,
} from 'tamagui'
import { useOrderDetails } from '@contexts/OrderDetailsContext'
import { useCallback, useEffect, useState } from 'react'
import { useToastController } from '@tamagui/toast'
import { useSession } from '@hooks/useSession'
import { useRudderstack } from '@hooks/useAnalytics'
import { useThemeParams } from '@hooks/useThemeParams'

type ChangeCreditCardModalProps = {
  open: boolean
  onClose: () => void
}

export function ChangeCreditCardModal({
  open,
  onClose,
}: ChangeCreditCardModalProps) {
  const { data: params } = useThemeParams()
  const { t } = useTranslation('OrderDetails', {
    keyPrefix: 'subscription.changeCreditCard',
  })
  const {
    id: orderId,
    productTitle,
    subscription,
    createdAt,
  } = useOrderDetails()
  const { refetch } = useOrderDetails()
  const toast = useToastController()
  const { track } = useRudderstack()
  const [isModalReady, setIsModalReady] = useState(false)
  const [updateCardUrl, setUpdateCardUrl] = useState('')

  const handleSuccess = useCallback(async () => {
    await refetch()
    toast.show(t('toast.success.title'), {
      message: t('toast.success.description'),
    })
    track('Credit Card Updated by Student', {
      product_name: productTitle,
      subscription_id: subscription?.id,
      subscription_status: subscription?.status,
      subscription_type: subscription?.subscription_type,
      cart_id: orderId,
      cart_created_at: createdAt,
      school_id: params?.school?.id,
    })
    onClose()
  }, [
    refetch,
    onClose,
    t,
    toast,
    track,
    productTitle,
    subscription,
    orderId,
    createdAt,
    params,
  ])

  const handleError = useCallback(
    (error?: Error) => {
      toast.show(t('toast.error.title'), {
        message: t('toast.error.description'),
        type: 'error',
      })
      console.error(error)
      onClose()
    },
    [onClose, t, toast],
  )

  useEffect(() => {
    const checkoutOrigin = process.env.EXPO_PUBLIC_SPARKCHECKOUT_API_URL

    function handleReceiveMessage(event: MessageEvent) {
      // A tela embutida agora roda adyen-web + carteiras, que tambem postam mensagens pro parent.
      // So agimos em mensagens vindas do iframe do sparkcheckout e com o formato esperado, para nao
      // confirmar/errar a troca por uma mensagem de terceiro.
      if (checkoutOrigin && event.origin !== new URL(checkoutOrigin).origin)
        return

      if (event.data?.status === 'success') {
        handleSuccess()
      } else if (event.data?.status === 'error') {
        handleError()
      }
    }

    window.addEventListener('message', handleReceiveMessage)

    return () => {
      window.removeEventListener('message', handleReceiveMessage)
    }
  }, [handleError, handleSuccess])

  const { token } = useSession()
  const { height } = useWindowDimensions()

  // Controla o timing de inicialização do modal no mobile
  useEffect(() => {
    if (open && token && orderId) {
      const timer = setTimeout(() => {
        setUpdateCardUrl(
          `${process.env.EXPO_PUBLIC_SPARKCHECKOUT_API_URL}/drop-ins/update-credit-card/?token=${token}&orderId=${orderId}`,
        )
        setIsModalReady(true)
      }, 150)

      return () => clearTimeout(timer)
    } else if (!open) {
      setIsModalReady(false)
      setUpdateCardUrl('')
    }
  }, [open, token, orderId])

  useEffect(() => {
    if (open) {
      // Força uma atualização do estado para garantir renderização
      const forceUpdate = setTimeout(() => {
        setIsModalReady((prev) => prev)
      }, 50)

      return () => clearTimeout(forceUpdate)
    }
  }, [open])

  const handleCloseModal = useCallback(() => {
    setIsModalReady(false)
    setUpdateCardUrl('')
    onClose()
  }, [onClose])

  return (
    <Modal
      animation="medium"
      backdropOpacity={0.8}
      onClose={handleCloseModal}
      open={open}
      snapPoints={[95, 100]}
      snapPointsMode="percent"
      shouldAdapt={true}
      whenShouldAdapt="sm"
      dismissOnOverlayPress={false}
      moveOnKeyboardChange
    >
      <YStack
        gap="$size.spacing-xs"
        paddingHorizontal="$size.spacing-lg"
        paddingVertical="$size.spacing-xs"
        $gtSm={{
          p: '$size.spacing-lg',
          width: 600,
        }}
        height="100%"
      >
        <YStack gap="$size.spacing-lg" height="100%">
          <Dialog.Title
            color="$text-primary"
            ff="$mulishBold"
            fontSize="$font-size-sm"
            textAlign="center"
          >
            {t('title')}
          </Dialog.Title>

          <XStack
            height={height - 150}
            $gtSm={{ height: 385 }}
            overflow="hidden"
            br="$border-radius-sm"
            justifyContent="center"
            alignItems="center"
          >
            {!isModalReady || !updateCardUrl ? (
              <YStack gap="$spacing-xs" alignItems="center">
                <Spinner size="large" color="$icon-secondary" />
                <Text color="$text-secondary" fontSize="$font-size-xs">
                  {t('loading', 'Carregando...')}
                </Text>
              </YStack>
            ) : (
              <iframe
                src={updateCardUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                allowFullScreen
                // Apple Pay JS e Payment Request (Google Pay) so funcionam em iframe com a
                // permission policy "payment"; sem isto os botoes da carteira aparecem mas a
                // folha nao abre.
                allow="payment"
                loading="lazy"
                // allow-popups/allow-popups-to-escape-sandbox: a folha do Google Pay abre um popup
                // (pay.google.com) que o sandbox bloquearia; sem isto a folha fecha na hora.
                sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation allow-popups allow-popups-to-escape-sandbox"
              />
            )}
          </XStack>
        </YStack>
      </YStack>
    </Modal>
  )
}
